/**
 * The sculpt-neighbour graph: which geometric corners are adjacent for the
 * purpose of shaping, as opposed to which are adjacent for the purpose of
 * showing a user an edge they can bevel.
 *
 * WHY THIS IS NOT `corners[].edgeIds`. `computeGeometricAdjacency` deliberately
 * omits coplanar triangulation diagonals from its geometric edges — a raw edge
 * whose two adjacent faces sit in the same region is reported separately in
 * `diagonalTopologyEdgeIds` and never becomes a geometric edge. That is exactly
 * right for its purpose: `bevelEdge` must never be handed the hidden diagonal
 * inside a flat quad.
 *
 * It is wrong as a sculpt neighbourhood. A vertex in the interior of a flat
 * region reaches its region-mates *only* through those diagonals, so falloff or
 * ring expansion built on `edgeIds` would have to leak around the rim of a flat
 * face instead of crossing it. Walking every raw face edge instead adds exactly
 * the omitted diagonals — measured on stock primitives: box 18 edges vs 12,
 * sphere 126 vs 94, cylinder 75 vs 63, and on a box those 6 extra edges are
 * precisely its 6 `diagonalTopologyEdgeIds`.
 *
 * ORPHAN VERTICES ARE A SEPARATE PROBLEM, and this module does not solve them.
 * `THREE.SphereGeometry` writes its two pole vertices into the position buffer
 * but never references them from the index buffer, and `captureTopology` walks
 * the position buffer — so a stock sphere carries two vertices belonging to no
 * face at all. They are isolated here and in the geometric-edge graph alike,
 * because no face-edge graph can reach a vertex that has no faces.
 *
 * IDENTITY AND GUARDING. The graph's *edge set* is a pure function of the index
 * buffer, so it does not move when positions do. Its *node identity* is not:
 * both endpoints are mapped through corner membership, which
 * `computeGeometricAdjacency` derives from exact position coincidence. A purely
 * positional edit can therefore merge or split a corner group and change the
 * resolved graph while raw topology is byte-identical. Anything that caches or
 * guards graph addressing must pin BOTH `rawTopologyFingerprint` (the edge set)
 * and `cornerFingerprint` (the node identity).
 *
 * Read-only and non-persisted, exactly like the adjacency layer it builds on:
 * recompute after any topology change rather than trying to keep one in sync.
 */

/** Sorted-pair key, so an undirected edge has exactly one entry. */
export function sculptEdgeKey(cornerA, cornerB) {
  return cornerA < cornerB ? `${cornerA} ${cornerB}` : `${cornerB} ${cornerA}`;
}

function distance(a, b) {
  return Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
}

/**
 * Build the sculpt-neighbour graph over geometric corners.
 *
 * `corners` is `computeGeometricAdjacency(topology).corners` — passed in rather
 * than recomputed here on purpose. Adjacency throws on a mesh containing a
 * degenerate face (see the degenerate-face audit), and a caller that already
 * holds an adjacency result should not pay for a second traversal or acquire a
 * hidden second failure mode.
 *
 * Returns `{ nodes, edgeLength }`:
 *   nodes       Map<cornerId, Set<cornerId>>  neighbours, insertion-sorted
 *   edgeLength  Map<edgeKey, number>          object-local Euclidean length
 *
 * Lengths are in the object's LOCAL space, the same space topology positions
 * live in — which is also the space a relative `delta` is expressed in by
 * default, so a falloff radius and a displacement share units. Under a
 * non-uniform object scale that is not world distance; that is a deliberate
 * choice, documented at the falloff layer that consumes it.
 */
export function buildSculptGraph(topology, corners) {
  if (!topology || !Array.isArray(topology.vertices) || !Array.isArray(topology.faces)) {
    throw new Error('buildSculptGraph requires topology vertices and faces arrays');
  }
  if (!Array.isArray(corners)) {
    throw new Error('buildSculptGraph requires the corners array from computeGeometricAdjacency');
  }

  const cornerIdByVertexId = new Map();
  for (const corner of corners) {
    for (const vertexId of corner.vertexIds) cornerIdByVertexId.set(vertexId, corner.id);
  }

  const positionById = new Map(topology.vertices.map((vertex) => [vertex.id, vertex.position]));
  const neighbours = new Map(corners.map((corner) => [corner.id, new Set()]));
  const edgeLength = new Map();

  for (const face of topology.faces) {
    const ids = face.vertexIds;
    for (let i = 0; i < ids.length; i++) {
      const fromVertex = ids[i];
      const toVertex = ids[(i + 1) % ids.length];
      const from = cornerIdByVertexId.get(fromVertex);
      const to = cornerIdByVertexId.get(toVertex);
      if (from === undefined || to === undefined) {
        // Corner coverage is total — union-find is seeded with every topology
        // vertex — so a miss means `corners` came from a different topology
        // than the one passed in. Fail loudly rather than build a graph with
        // silently absent nodes.
        throw new Error(
          `buildSculptGraph: vertex '${from === undefined ? fromVertex : toVertex}' has no corner; `
          + 'corners must come from computeGeometricAdjacency on this same topology',
        );
      }
      // A face edge whose endpoints are welded into ONE corner is a self-loop
      // at that node. It carries no neighbourhood information and would give a
      // zero-length edge that a shortest-path walk has no use for.
      if (from === to) continue;

      neighbours.get(from).add(to);
      neighbours.get(to).add(from);

      const key = sculptEdgeKey(from, to);
      if (!edgeLength.has(key)) {
        // Every member of a corner is exact-position-equal by construction, so
        // any member's position is the corner's position.
        edgeLength.set(key, distance(positionById.get(fromVertex), positionById.get(toVertex)));
      }
    }
  }

  // Materialise in sorted order so two builds of the same topology serialise
  // identically — the determinism this layer's consumers rely on.
  const sortedNodes = new Map(
    [...neighbours.keys()].sort().map((id) => [id, new Set([...neighbours.get(id)].sort())]),
  );
  const sortedLengths = new Map([...edgeLength.keys()].sort().map((key) => [key, edgeLength.get(key)]));

  return { nodes: sortedNodes, edgeLength: sortedLengths };
}
